import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { environment } from '../environments/environment';
import { ApiService } from './core/api/api.service';
import { LoaderComponent } from './shared/components/loader/loader.component';

const PROJECT_TITLES: Record<string, string> = {
  plusphysio:   'PlusPhysio',
  plusdoctor:   'PlusDoctor',
  plusradiology: 'PlusRadiology',
  pluspedia: 'PlusPedia',
  plusgynecologist: 'PlusGynaecologist',
};

/** PWA manifest served per brand — see public/manifest-<brand>.webmanifest. */
const PROJECT_MANIFESTS: Record<string, string> = {
  plusphysio: 'manifest-plusphysio.webmanifest',
  plusdoctor: 'manifest-plusdoctor.webmanifest',
  plusradiology: 'manifest-plusradiology.webmanifest',
  pluspedia: 'manifest-pluspedia.webmanifest',
};

/** Browser tab / install-prompt icon per brand — see public/assets/media/logos/.
 *  Pluspedia has no dedicated .ico yet, so it reuses the same .png the PWA manifest's
 *  icon list already points at (public/manifest-pluspedia.webmanifest). Plusgynecologist
 *  likewise reuses its placeholder logo SVG until a real brand asset exists. */
const PROJECT_FAVICONS: Record<string, string> = {
  plusphysio: 'assets/media/logos/faviconn.ico',
  plusdoctor: 'assets/media/logos/plusdoctorfavicon.ico',
  plusradiology: 'assets/media/logos/radiology-favicon.ico',
  pluspedia: 'assets/plus_pedia_logo.png',
  plusgynecologist: 'assets/plus_gynecologist_mini.png',
};

/** MIME type per favicon file extension — kept accurate so browsers that honour the
 *  `<link type>` attribute don't reject a non-.ico favicon (e.g. Pluspedia's .png). */
const FAVICON_MIME_TYPES: Record<string, string> = {
  ico: 'image/x-icon',
  png: 'image/png',
  svg: 'image/svg+xml',
  webp: 'image/webp',
};

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, LoaderComponent],
  template: '<router-outlet /><app-loader [show]="isLoading()" />',
})
export class AppComponent {
  private readonly doc = inject(DOCUMENT);
  private readonly title = inject(Title);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  /** True while a route transition (lazy chunk fetch, guards, resolvers) is in flight. */
  private readonly navigationInProgress = signal(false);

  /**
   * Drives the global overlay loader. Stays visible while the route is still
   * loading (chunk fetch/guards/resolvers) and/or while any HTTP request
   * triggered by the activated component is still pending, so the spinner
   * only hides once the whole navigation — route + its API calls — settles,
   * regardless of whether those calls succeed or fail.
   */
  readonly isLoading = computed(() => this.navigationInProgress() || this.api.isLoading());

  constructor() {
    this.doc.documentElement.setAttribute('data-project', environment.environmentName);
    this.title.setTitle(PROJECT_TITLES[environment.environmentName] ?? environment.environmentName);
    this.registerManifest();
    this.setFavicon(PROJECT_FAVICONS[environment.environmentName]);

    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigationInProgress.set(true);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.navigationInProgress.set(false);
      }
    });
  }

  /** Points the browser's install-prompt manifest at this build's brand — see PROJECT_MANIFESTS. */
  private registerManifest(): void {
    const manifestFile = PROJECT_MANIFESTS[environment.environmentName];
    if (!manifestFile) return;

    this.doc.querySelector('link[rel="manifest"]')?.remove();

    const link = this.doc.createElement('link');
    link.setAttribute('rel', 'manifest');
    link.setAttribute('href', manifestFile);
    this.doc.head.appendChild(link);
  }

  private setFavicon(faviconUrl: string | undefined): void {
    if (!faviconUrl) return;

    let link = this.doc.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.rel = 'icon';
      this.doc.head.appendChild(link);
    }
    link.href = faviconUrl;
    link.type = this.resolveFaviconMimeType(faviconUrl);
  }

  private resolveFaviconMimeType(faviconUrl: string): string {
    const extension = faviconUrl.split('.').pop()?.toLowerCase() ?? '';
    return FAVICON_MIME_TYPES[extension] ?? FAVICON_MIME_TYPES['ico'];
  }
}
